/**
 * Compute (D447): the Kubernetes clusters the organisation has approved, and the requests people make to have more
 * approved. Two places in a secondary nav; each is a header, its figures, then its lists. Both belong to those who look
 * after nodes (`machines.nodes`); with roles off, to administrators.
 */

import { Navigate, useParams } from 'react-router-dom';

import { ClustersPanel } from '../components/compute/ClustersPanel.tsx';
import { RequestsPanel } from '../components/compute/RequestsPanel.tsx';
import { TONE } from '../components/compute/computeWords.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { SubnavLayout, type SubnavItem } from '../components/ui/Layout.tsx';
import { holds, sees, usePermissions } from '../permissions.ts';

type Tab = 'clusters' | 'requests';

export function ComputePage() {
  const { tab } = useParams<{ tab?: string }>();
  const perms = usePermissions();
  const p = perms.data;
  const roles = p?.mode === 'roles';
  const nodes = !roles || sees(p, 'machines.nodes');
  const shown: Array<SubnavItem & { key: Tab }> = nodes
    ? [
        { key: 'clusters', label: 'Clusters', to: '/compute' },
        { key: 'requests', label: 'Requests', to: '/compute/requests' },
      ]
    : [];
  if (perms.loading && !p) return null;
  const current = (tab ?? 'clusters') as Tab;
  if (!shown.length) return <Navigate to="/" replace />;
  if (!shown.some((s) => s.key === current)) return <Navigate to={shown[0]!.to} replace />;

  // Roles on, a change needs the verb: the API still decides, but the buttons are not offered to a reader.
  const mayEdit = !roles || holds(p, 'machines.nodes', 'act');

  return (
    <SubnavLayout label="Compute" groups={[{ label: '', items: shown }]} current={current}>
      <PartBoundary key={current} name="Compute" tone={TONE}>
        {current === 'clusters' ? <ClustersPanel mayEdit={mayEdit} /> : <RequestsPanel mayEdit={mayEdit} />}
      </PartBoundary>
    </SubnavLayout>
  );
}
